import React from "react";
import { Typography, Button, Row, Col } from "antd";
import {
  InfoCircleOutlined,
  CheckCircleFilled,
  LineChartOutlined,
  CrownOutlined,
} from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilm,
  faLightbulb,
  faVideo,
} from "@fortawesome/free-solid-svg-icons";

interface PromotionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  bullets?: (string | { icon: React.ReactNode; text: string })[];
  tags?: string[];
  deliverables?: React.ReactNode[];
  isSelected?: boolean;
  onClick?: () => void;
  requirement?: string;
  checkmark?: boolean;
  style?: React.CSSProperties;
}

// PromotionCard: reusable for both steps
export function PromotionCard({
  icon,
  title,
  description,
  bullets,
  tags,
  deliverables,
  isSelected,
  onClick,
  requirement,
  checkmark,
  style,
}: PromotionCardProps) {
  return (
    <div
      className={`promotion-card${isSelected ? " selected" : ""}`}
      onClick={onClick}
      style={{ cursor: "pointer", ...style }}
    >
      {isSelected && checkmark !== false && (
        <span className="checkmark">
          <CheckCircleFilled style={{ color: "#ffac39", fontSize: 20 }} />
        </span>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span className="promotion-icon">{icon}</span>
        <span className="promotion-title">{title}</span>
      </div>
      <span
        style={{
          fontSize: 18,
          color: "#1d2023b3",
        }}
      >
        {description}
      </span>
      {bullets && (
        <ul
          style={{ margin: 0, paddingLeft: 0, marginBottom: 20, marginTop: 20 }}
        >
          {bullets.map((b, idx) => (
            <li
              key={idx}
              style={{
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
              className="text-regular"
            >
              {typeof b === "string" ? (
                b
              ) : (
                <>
                  <span>{b.icon}</span> {b.text}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {deliverables && (
        <>
          <div style={{ fontSize: 18, marginBottom: 12 }} className="header-2">
            Deliverables:
          </div>
          <div
            style={{
              color: "#1d2023b3",
              fontSize: 16,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            {deliverables.map((d, i) => (
              <div key={i}>{d}</div>
            ))}
          </div>
        </>
      )}
      {tags && (
        <div className="promotion-tags">
          {tags.map((tag) => (
            <span className="promotion-tag" key={tag}>
              {tag}
            </span>
          ))}
        </div>
      )}
      {requirement && (
        <span
          style={{
            display: "block",
            marginTop: 8,
            color: "#b0a990",
            fontWeight: 500,
          }}
        >
          <InfoCircleOutlined /> {requirement}
        </span>
      )}
    </div>
  );
}

interface PromotionTypeStepProps {
  cardData: Array<{
    key: string;
    icon: React.ReactNode;
    title: string;
    description: string;
    bullets: (string | { icon: React.ReactNode; text: string })[];
    tags: string[];
    requirement?: string;
  }>;
  selectedKey: string | undefined;
  onSelect: (key: string) => void;
  onContinue: () => void;
  onBack: () => void;
  startingStepNumber?: number;
  totalSteps?: number;
}

interface PromotionChannelStepProps {
  selectedChannel?: string;
  setSelectedChannel: (key: string) => void;
  onContinue: () => void;
  onBack: () => void;
  startingStepNumber?: number;
  totalSteps?: number;
}

// PromotionTypeStep: promotion type selection
export function PromotionTypeStep({
  cardData,
  selectedKey,
  onSelect,
  onContinue,
  onBack,
  startingStepNumber,
  totalSteps,
}: PromotionTypeStepProps) {
  return (
    <>
      <Row>
        <Col xs={16}>
          <Typography.Title level={3} style={{ marginBottom: 0 }}>
            What type of collaboration do you want to run?
          </Typography.Title>
          <Typography.Text style={{ color: "#1d2023b3", fontSize: 18 }}>
            Choose which collaboration type best fits your current marketing
            goals
          </Typography.Text>
        </Col>
        {startingStepNumber && (
          <Col
            xs={8}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              color: "#666666",
            }}
          >
            Step {startingStepNumber} / {totalSteps}
          </Col>
        )}
      </Row>
      <div className="promotions-grid">
        {cardData.map((item) => (
          <PromotionCard
            key={item.key}
            icon={item.icon}
            title={item.title}
            description={item.description}
            bullets={item.bullets}
            tags={item.tags}
            isSelected={selectedKey === item.key}
            onClick={() => onSelect(item.key)}
            requirement={item.requirement}
          />
        ))}
      </div>
      <div
        style={{
          background: "#fdf9f3",
          borderRadius: 12,
          padding: 20,
          marginTop: 32,
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
        }}
      >
        <FontAwesomeIcon
          icon={faLightbulb}
          style={{ color: "#ffac39", fontSize: 20 }}
        />
        <div>
          <div style={{ fontSize: 20, fontWeight: 500, marginBottom: 2 }}>
            Why choose one collaboration type?
          </div>
          <div style={{ fontSize: 17 }}>
            Each collaboration type serves a different goal. For best results,
            choose the option that aligns with your current priority, whether
            it's driving sales now or building long-term brand visibility.
          </div>
        </div>
      </div>
      <Row style={{ gap: 32, marginTop: 64, justifyContent: "space-between" }}>
        <Button
          onClick={onBack}
          className="text-strong-large"
          htmlType="button"
        >
          Back
        </Button>

        <Button
          type="primary"
          disabled={!selectedKey}
          onClick={onContinue}
          className="label-strong"
          htmlType="button"
        >
          Continue
        </Button>
      </Row>
    </>
  );
}

// PromotionChannelStep: channel selection
export function PromotionChannelStep({
  selectedChannel,
  setSelectedChannel,
  onContinue,
  onBack,
  startingStepNumber,
  totalSteps,
}: PromotionChannelStepProps) {
  const channelOptions = [
    {
      key: "instagram",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="27"
          height="30"
          viewBox="0 0 27 30"
          fill="none"
        >
          <g clip-path="url(#clip0_99_4409)">
            <path
              d="M13.1309 8.26172C9.4043 8.26172 6.39844 11.2676 6.39844 14.9941C6.39844 18.7207 9.4043 21.7266 13.1309 21.7266C16.8574 21.7266 19.8633 18.7207 19.8633 14.9941C19.8633 11.2676 16.8574 8.26172 13.1309 8.26172ZM13.1309 19.3711C10.7227 19.3711 8.75391 17.4082 8.75391 14.9941C8.75391 12.5801 10.7168 10.6172 13.1309 10.6172C15.5449 10.6172 17.5078 12.5801 17.5078 14.9941C17.5078 17.4082 15.5391 19.3711 13.1309 19.3711ZM21.709 7.98633C21.709 8.85938 21.0059 9.55664 20.1387 9.55664C19.2656 9.55664 18.5684 8.85352 18.5684 7.98633C18.5684 7.11914 19.2715 6.41602 20.1387 6.41602C21.0059 6.41602 21.709 7.11914 21.709 7.98633ZM26.168 9.58008C26.0684 7.47656 25.5879 5.61328 24.0469 4.07812C22.5117 2.54297 20.6484 2.0625 18.5449 1.95703C16.377 1.83398 9.87891 1.83398 7.71094 1.95703C5.61328 2.05664 3.75 2.53711 2.20898 4.07227C0.667969 5.60742 0.193359 7.4707 0.0878906 9.57422C-0.0351562 11.7422 -0.0351562 18.2402 0.0878906 20.4082C0.1875 22.5117 0.667969 24.375 2.20898 25.9102C3.75 27.4453 5.60742 27.9258 7.71094 28.0312C9.87891 28.1543 16.377 28.1543 18.5449 28.0312C20.6484 27.9316 22.5117 27.4512 24.0469 25.9102C25.582 24.375 26.0625 22.5117 26.168 20.4082C26.291 18.2402 26.291 11.748 26.168 9.58008ZM23.3672 22.7344C22.9102 23.8828 22.0254 24.7676 20.8711 25.2305C19.1426 25.916 15.041 25.7578 13.1309 25.7578C11.2207 25.7578 7.11328 25.9102 5.39062 25.2305C4.24219 24.7734 3.35742 23.8887 2.89453 22.7344C2.20898 21.0059 2.36719 16.9043 2.36719 14.9941C2.36719 13.084 2.21484 8.97656 2.89453 7.25391C3.35156 6.10547 4.23633 5.2207 5.39062 4.75781C7.11914 4.07227 11.2207 4.23047 13.1309 4.23047C15.041 4.23047 19.1484 4.07812 20.8711 4.75781C22.0195 5.21484 22.9043 6.09961 23.3672 7.25391C24.0527 8.98242 23.8945 13.084 23.8945 14.9941C23.8945 16.9043 24.0527 21.0117 23.3672 22.7344Z"
              fill="#1D2023"
            />
          </g>
          <defs>
            <clipPath id="clip0_99_4409">
              <path d="M0 0H26.25V30H0V0Z" fill="white" />
            </clipPath>
          </defs>
        </svg>
      ),
      title: "Instagram",
      description:
        "Focus on visual storytelling, food and lifestyle content to showcase your brand's atmosphere and dishes",
      bullets: [
        {
          icon: (
            <LineChartOutlined style={{ color: "#ff9f1c", fontSize: 20 }} />
          ),
          text: "Consistent engagement rates (2-5%)",
        },
        {
          icon: <CrownOutlined style={{ color: "#ff9f1c", fontSize: 20 }} />,
          text: "Gen X, Young Professionals & Family Demographics",
        },
      ],
      deliverables: [
        <span>
          <FontAwesomeIcon icon={faVideo} className="orange" /> Instagram Reel
        </span>,
        <span>
          <FontAwesomeIcon icon={faFilm} className="orange" /> Instagram Story
        </span>,
      ],
    },
    {
      key: "tiktok",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="27"
          height="30"
          viewBox="0 0 27 30"
          fill="none"
        >
          <path
            d="M26.25 12.2994C23.6704 12.3056 21.154 11.5011 19.0564 9.99961V20.4715C19.0557 22.411 18.4629 24.3041 17.3572 25.8975C16.2516 27.491 14.6858 28.709 12.8692 29.3886C11.0527 30.0682 9.07197 30.177 7.19191 29.7005C5.31186 29.2239 3.62209 28.1848 2.34855 26.722C1.07501 25.2592 0.278414 23.4425 0.0652669 21.5147C-0.14788 19.5869 0.232585 17.6401 1.15579 15.9344C2.079 14.2287 3.50093 12.8455 5.23147 11.9698C6.96201 11.094 8.91867 10.7675 10.8398 11.0338V16.3008C9.96069 16.0243 9.01668 16.0326 8.14258 16.3246C7.26848 16.6166 6.509 17.1773 5.97259 17.9267C5.43618 18.6761 5.15027 19.5758 5.1557 20.4973C5.16113 21.4189 5.45761 22.3152 6.00281 23.0582C6.54801 23.8012 7.31405 24.3529 8.19153 24.6346C9.069 24.9163 10.013 24.9135 10.8888 24.6266C11.7646 24.3398 12.5274 23.7835 13.0682 23.0373C13.609 22.2911 13.9002 21.3931 13.9002 20.4715V0H19.0564C19.0528 0.435434 19.0893 0.870274 19.1654 1.29902C19.3446 2.25614 19.7171 3.16665 20.2602 3.97484C20.8034 4.78304 21.5057 5.47195 22.3242 5.99941C23.4887 6.76939 24.8539 7.17979 26.25 7.17949V12.2994Z"
            fill="#1D2023"
          />
        </svg>
      ),
      title: "TikTok",
      description:
        "Leverage short-form video content to create engaging and potential viral-worthy food experiences",
      bullets: [
        {
          icon: (
            <LineChartOutlined style={{ color: "#ff9f1c", fontSize: 20 }} />
          ),
          text: "Variable results (0.5-15% engagement)",
        },
        {
          icon: <CrownOutlined style={{ color: "#ff9f1c", fontSize: 20 }} />,
          text: "Gen Z & Young Millennial Demographics",
        },
      ],
      deliverables: [
        <span>
          <FontAwesomeIcon icon={faVideo} className="orange" /> TikTok Video
        </span>,
      ],
    },
  ];
  return (
    <>
      <Row>
        <Col xs={16}>
          <Typography.Title level={3} style={{ marginBottom: 0 }}>
            Select Promotion Channel
          </Typography.Title>
        </Col>
        {startingStepNumber && (
          <Col
            xs={8}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              color: "#666666",
            }}
          >
            Step {startingStepNumber + 1} / {totalSteps}
          </Col>
        )}
      </Row>

      <Typography.Text style={{ color: "#1d2023b3", fontSize: 16 }}>
        Choose which platform you want to focus your influencer efforts
      </Typography.Text>
      <div className="promotions-grid">
        {channelOptions.map((ch) => (
          <PromotionCard
            key={ch.key}
            icon={ch.icon}
            title={ch.title}
            description={ch.description}
            bullets={ch.bullets}
            deliverables={ch.deliverables}
            isSelected={selectedChannel === ch.key}
            onClick={() => setSelectedChannel(ch.key)}
            style={{ minHeight: 460 }}
          />
        ))}
      </div>
      <div
        style={{
          background: "#fdf9f3",
          borderRadius: 12,
          padding: 20,
          marginTop: 32,
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
        }}
      >
        <FontAwesomeIcon
          icon={faLightbulb}
          style={{ color: "#ffac39", fontSize: 20 }}
        />
        <div>
          <div style={{ fontSize: 20, fontWeight: 500, marginBottom: 2 }}>
            Why choose one platform?
          </div>
          <div style={{ fontSize: 17 }}>
            Influencers typically specialize in one platform. For optimal
            results, it's recommended to create separate collaborations tailored
            to each channel.
          </div>
        </div>
      </div>
      <Row style={{ gap: 32, marginTop: 32, justifyContent: "space-between" }}>
        <Button
          onClick={onBack}
          className="text-strong-large"
          htmlType="button"
        >
          Back
        </Button>

        <Button
          type="primary"
          disabled={!selectedChannel}
          onClick={onContinue}
          className="label-strong"
          htmlType="button"
        >
          Continue
        </Button>
      </Row>
    </>
  );
}
